import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Search, 
  Plus, 
  User, 
  Calendar as CalendarIcon, 
  Phone,
  Mail,
  MapPin,
  FileText,
  MoreHorizontal,
  Eye,
  Users,
  Home,
  Baby,
  Trash2
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { EditPermissionApprovalPanel } from '../components/EditPermissionManager';
import { PatientViewApprovalPanel } from '../components/PatientViewApproval';
import { toast } from 'sonner';

export default function PatientsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Child Info, 2: Family Info, 3: GP/School Info
  
  const [formData, setFormData] = useState({
    // Child Information
    first_name: '',
    last_name: '',
    date_of_birth: '',
    sex: 'Male',
    nhs_number: '',
    ethnic_group: '',
    primary_language: 'English',
    diagnoses: '',
    
    // Family Information
    family_name: '',
    father_name: '',
    father_phone: '',
    father_email: '',
    father_occupation: '',
    mother_name: '',
    mother_phone: '',
    mother_email: '',
    mother_occupation: '',
    child_lives_with: 'both_parents',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    family_notes: '',
    
    // School & GP Information
    school: '',
    school_year: '',
    school_class: '',
    school_contact: '',
    school_phone: '',
    gp_name: '',
    gp_practice: '',
    gp_address: '',
    gp_phone: '',
    gp_email: '',
    council: '',
    social_worker: '',
    social_worker_phone: ''
  });

  useEffect(() => {
    fetchPatients();
  }, [searchQuery]);

  const fetchPatients = async () => {
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await api.get('/patients', { params });
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  // Delete patient (Super Admin / Data Record Officer ONLY)
  const handleDeletePatient = async (patientId, patientName) => {
    if (!window.confirm(`PERMANENT DELETION: Are you sure you want to delete "${patientName}"?\n\nAs the Data Record Officer, this will permanently remove:\n• Patient record\n• All progress notes\n• All evaluations\n• All attachments\n\nThis action cannot be undone.`)) return;
    try {
      await api.delete(`/patients/${patientId}`);
      toast.success(`Patient "${patientName}" deleted by Data Record Officer`);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete patient');
    }
  };

  // Quick Add Mode for busy clinics
  const [quickAddMode, setQuickAddMode] = useState(false);

  const handleQuickAdd = async () => {
    // Validate minimum required fields for quick add
    if (!formData.first_name || !formData.last_name || !formData.date_of_birth) {
      toast.error('Quick Add requires: First Name, Last Name, and Date of Birth');
      return;
    }
    
    try {
      // Create minimal family record
      const familyResponse = await api.post('/families', {
        family_name: `${formData.last_name} Family`,
        address: formData.address || ''
      });
      
      // Create patient with minimal info
      await api.post('/patients', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth,
        sex: formData.sex || 'Not specified',
        family_id: familyResponse.data.id,
        primary_language: 'English'
      });
      
      toast.success('Child registered successfully! You can add more details later by editing the record.');
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to quick-add patient');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Enhanced validation with specific error messages
    const errors = [];
    if (!formData.first_name?.trim()) errors.push('First Name');
    if (!formData.last_name?.trim()) errors.push('Last Name');
    if (!formData.date_of_birth) errors.push('Date of Birth');
    
    if (errors.length > 0) {
      toast.error(`Please complete required fields: ${errors.join(', ')}`, {
        description: 'Required fields are marked with * in Step 1'
      });
      setCurrentStep(1);
      return;
    }
    
    // Validate date of birth is in the past
    if (new Date(formData.date_of_birth) > new Date()) {
      toast.error('Date of Birth cannot be in the future');
      setCurrentStep(1);
      return;
    }
    
    try {
      // First create the family record
      const familyPayload = {
        family_name: formData.family_name || `${formData.last_name} Family`,
        father_name: formData.father_name,
        father_phone: formData.father_phone,
        father_email: formData.father_email,
        mother_name: formData.mother_name,
        mother_phone: formData.mother_phone,
        mother_email: formData.mother_email,
        address: formData.address,
        child_lives_with: formData.child_lives_with,
        notes: formData.family_notes
      };
      
      const familyResponse = await api.post('/families', familyPayload);
      const familyId = familyResponse.data.id;
      
      // Then create the patient linked to the family
      const patientPayload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth,
        sex: formData.sex,
        nhs_number: formData.nhs_number,
        school: formData.school,
        school_year: formData.school_year,
        school_class: formData.school_class,
        gp_name: formData.gp_name,
        gp_address: formData.gp_address,
        gp_phone: formData.gp_phone,
        gp_email: formData.gp_email,
        council: formData.council,
        address: formData.address,
        ethnic_group: formData.ethnic_group,
        primary_language: formData.primary_language,
        diagnoses: formData.diagnoses ? formData.diagnoses.split(',').map(d => d.trim()) : [],
        family_id: familyId,
        // Additional fields
        father_name: formData.father_name,
        father_phone: formData.father_phone,
        father_email: formData.father_email,
        mother_name: formData.mother_name,
        mother_phone: formData.mother_phone,
        mother_email: formData.mother_email,
        emergency_contact: formData.emergency_contact,
        emergency_phone: formData.emergency_phone
      };
      
      await api.post('/patients', patientPayload);
      toast.success('Child and family registered successfully');
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      sex: 'Male',
      nhs_number: '',
      ethnic_group: '',
      primary_language: 'English',
      diagnoses: '',
      family_name: '',
      father_name: '',
      father_phone: '',
      father_email: '',
      father_occupation: '',
      mother_name: '',
      mother_phone: '',
      mother_email: '',
      mother_occupation: '',
      child_lives_with: 'both_parents',
      address: '',
      emergency_contact: '',
      emergency_phone: '',
      family_notes: '',
      school: '',
      school_year: '',
      school_class: '',
      school_contact: '',
      school_phone: '',
      gp_name: '',
      gp_practice: '',
      gp_address: '',
      gp_phone: '',
      gp_email: '',
      council: '',
      social_worker: '',
      social_worker_phone: ''
    });
    setCurrentStep(1);
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const nextStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Edit Permission Approval Panel - For approvers */}
      <EditPermissionApprovalPanel />
      
      {/* Patient View Approval Panel - For Lead Therapists to approve cross-therapist access */}
      <PatientViewApprovalPanel />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Children & Families</h1>
          <p className="text-slate-500 mt-1">Manage child patients and their family information</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="add-patient-btn">
              <Plus className="w-4 h-4 mr-2" />
              Register Child
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Register New Child & Family</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${currentStep >= 1 ? 'bg-[#1e3a5f] text-white' : 'bg-slate-200'}`}>
                  <Baby className="w-4 h-4" /> Child
                </div>
                <div className="w-8 h-0.5 bg-slate-300"></div>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${currentStep >= 2 ? 'bg-[#1e3a5f] text-white' : 'bg-slate-200'}`}>
                  <Users className="w-4 h-4" /> Family
                </div>
                <div className="w-8 h-0.5 bg-slate-300"></div>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${currentStep >= 3 ? 'bg-[#1e3a5f] text-white' : 'bg-slate-200'}`}>
                  <Home className="w-4 h-4" /> School & GP
                </div>
              </div>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Step 1: Child Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1e3a5f] border-b pb-2">Child&apos;s Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        data-testid="patient-firstname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value, family_name: `${e.target.value} Family` })}
                        data-testid="patient-lastname"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dob">Date of Birth *</Label>
                      <Input
                        id="dob"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        data-testid="patient-dob"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sex *</Label>
                      <Select
                        value={formData.sex}
                        onValueChange={(value) => setFormData({ ...formData, sex: value })}
                      >
                        <SelectTrigger data-testid="patient-sex">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nhs">NHS Number</Label>
                      <Input
                        id="nhs"
                        value={formData.nhs_number}
                        onChange={(e) => setFormData({ ...formData, nhs_number: e.target.value })}
                        placeholder="123 456 7890"
                        data-testid="patient-nhs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ethnic">Ethnic Group</Label>
                      <Input
                        id="ethnic"
                        value={formData.ethnic_group}
                        onChange={(e) => setFormData({ ...formData, ethnic_group: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="language">Primary Language</Label>
                      <Input
                        id="language"
                        value={formData.primary_language}
                        onChange={(e) => setFormData({ ...formData, primary_language: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="diagnoses">Diagnoses (comma separated)</Label>
                    <Input
                      id="diagnoses"
                      value={formData.diagnoses}
                      onChange={(e) => setFormData({ ...formData, diagnoses: e.target.value })}
                      placeholder="Autism, ADHD, Dyslexia, etc."
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Family Information */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1e3a5f] border-b pb-2">Family Information</h3>
                  
                  <div className="space-y-2">
                    <Label>Child Lives With</Label>
                    <Select
                      value={formData.child_lives_with}
                      onValueChange={(value) => setFormData({ ...formData, child_lives_with: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both_parents">Both Parents</SelectItem>
                        <SelectItem value="mother">Mother Only</SelectItem>
                        <SelectItem value="father">Father Only</SelectItem>
                        <SelectItem value="guardian">Guardian</SelectItem>
                        <SelectItem value="foster">Foster Care</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium text-blue-800">Father / Guardian 1</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input
                          value={formData.father_name}
                          onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                          placeholder="Father's full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Occupation</Label>
                        <Input
                          value={formData.father_occupation}
                          onChange={(e) => setFormData({ ...formData, father_occupation: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          type="tel"
                          value={formData.father_phone}
                          onChange={(e) => setFormData({ ...formData, father_phone: e.target.value })}
                          placeholder="+44..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.father_email}
                          onChange={(e) => setFormData({ ...formData, father_email: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-pink-50 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium text-pink-800">Mother / Guardian 2</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input
                          value={formData.mother_name}
                          onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })}
                          placeholder="Mother's full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Occupation</Label>
                        <Input
                          value={formData.mother_occupation}
                          onChange={(e) => setFormData({ ...formData, mother_occupation: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          type="tel"
                          value={formData.mother_phone}
                          onChange={(e) => setFormData({ ...formData, mother_phone: e.target.value })}
                          placeholder="+44..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.mother_email}
                          onChange={(e) => setFormData({ ...formData, mother_email: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Home Address</Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      placeholder="Full home address..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Emergency Contact Name</Label>
                      <Input
                        value={formData.emergency_contact}
                        onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                        placeholder="Name and relationship"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Emergency Contact Phone</Label>
                      <Input
                        type="tel"
                        value={formData.emergency_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Family Notes</Label>
                    <Textarea
                      value={formData.family_notes}
                      onChange={(e) => setFormData({ ...formData, family_notes: e.target.value })}
                      rows={2}
                      placeholder="Any important family information..."
                    />
                  </div>
                </div>
              )}

              {/* Step 3: School & GP Information */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-[#1e3a5f] border-b pb-2">School Information</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="school">School Name</Label>
                      <Input
                        id="school"
                        value={formData.school}
                        onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        value={formData.school_year}
                        onChange={(e) => setFormData({ ...formData, school_year: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="class">Class</Label>
                      <Input
                        id="class"
                        value={formData.school_class}
                        onChange={(e) => setFormData({ ...formData, school_class: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>School Contact Person</Label>
                      <Input
                        value={formData.school_contact}
                        onChange={(e) => setFormData({ ...formData, school_contact: e.target.value })}
                        placeholder="SENCO / Teacher name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>School Phone</Label>
                      <Input
                        type="tel"
                        value={formData.school_phone}
                        onChange={(e) => setFormData({ ...formData, school_phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <h3 className="font-semibold text-[#1e3a5f] border-b pb-2 mt-6">GP Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gp_name">GP Name</Label>
                      <Input
                        id="gp_name"
                        value={formData.gp_name}
                        onChange={(e) => setFormData({ ...formData, gp_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp_practice">GP Practice</Label>
                      <Input
                        id="gp_practice"
                        value={formData.gp_practice}
                        onChange={(e) => setFormData({ ...formData, gp_practice: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gp_phone">GP Phone</Label>
                      <Input
                        id="gp_phone"
                        value={formData.gp_phone}
                        onChange={(e) => setFormData({ ...formData, gp_phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp_email">GP Email</Label>
                      <Input
                        id="gp_email"
                        type="email"
                        value={formData.gp_email}
                        onChange={(e) => setFormData({ ...formData, gp_email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>GP Address</Label>
                    <Textarea
                      value={formData.gp_address}
                      onChange={(e) => setFormData({ ...formData, gp_address: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <h3 className="font-semibold text-[#1e3a5f] border-b pb-2 mt-6">Local Authority</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="council">Council/LA</Label>
                      <Input
                        id="council"
                        value={formData.council}
                        onChange={(e) => setFormData({ ...formData, council: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Social Worker</Label>
                      <Input
                        value={formData.social_worker}
                        onChange={(e) => setFormData({ ...formData, social_worker: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SW Phone</Label>
                      <Input
                        type="tel"
                        value={formData.social_worker_phone}
                        onChange={(e) => setFormData({ ...formData, social_worker_phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="flex justify-between border-t pt-4">
                <div className="flex items-center gap-2">
                  {currentStep > 1 && (
                    <Button type="button" variant="outline" onClick={prevStep}>
                      Previous
                    </Button>
                  )}
                  {currentStep === 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      onClick={handleQuickAdd}
                      data-testid="quick-add-btn"
                    >
                      Quick Add (Minimal Info)
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  {currentStep < 3 ? (
                    <Button type="button" className="bg-[#1e3a5f]" onClick={nextStep}>
                      Next
                    </Button>
                  ) : (
                    <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" data-testid="patient-submit-btn">
                      Register Child & Family
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="card-icrcl">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search children by name, NHS number, or family name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="patient-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="text-lg">Registered Children</CardTitle>
          <CardDescription>All children and their family information</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : patients.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No children registered yet</p>
              <Button variant="link" className="text-[#2a9d8f]" onClick={() => setDialogOpen(true)}>
                Register your first child
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Child</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Parents/Guardians</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow key={patient.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-[#1e3a5f]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {patient.first_name} {patient.last_name}
                          </p>
                          <p className="text-xs text-slate-500">{patient.registration_number}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-center">
                        <p className="font-semibold text-[#1e3a5f]">{calculateAge(patient.date_of_birth)}</p>
                        <p className="text-xs text-slate-500">years</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {patient.father_name && <p className="text-blue-600">{patient.father_name}</p>}
                        {patient.mother_name && <p className="text-pink-600">{patient.mother_name}</p>}
                        {!patient.father_name && !patient.mother_name && <span className="text-slate-400">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {(patient.father_phone || patient.mother_phone) && (
                          <p className="flex items-center gap-1 text-slate-600">
                            <Phone className="w-3 h-3" />
                            {patient.father_phone || patient.mother_phone}
                          </p>
                        )}
                        {(patient.father_email || patient.mother_email) && (
                          <p className="flex items-center gap-1 text-slate-600 text-xs">
                            <Mail className="w-3 h-3" />
                            {patient.father_email || patient.mother_email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{patient.school || '-'}</span>
                      {patient.school_year && <span className="text-xs text-slate-500 block">Year {patient.school_year}</span>}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/patients/${patient.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/assessments/new?patient=${patient.id}`)}>
                            <FileText className="w-4 h-4 mr-2" />
                            New Assessment
                          </DropdownMenuItem>
                          {user?.email?.toLowerCase() === 'admin@icrcl.co.uk' && (
                            <DropdownMenuItem 
                              onClick={() => handleDeletePatient(patient.id, `${patient.first_name} ${patient.last_name}`)}
                              className="text-red-600 focus:text-red-600"
                              data-testid="delete-patient-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Patient (Data Officer)
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
